/**
 * Sección de BADGES (F3-C): muestra los logros concedidos por el servidor
 * (my_badges) con los visuales ligeros del kit de gamificación (emoji). Los
 * títulos/descripciones se traducen por su clave i18n estable.
 */
import { type Component, For, Show } from "solid-js";
import { msg } from "@lingui/core/macro";
import type { MessageDescriptor } from "@lingui/core";
import { badgeMeta } from "@space/shared";
import { useI18n } from "@/providers/i18n-provider";
import { useMyBadges } from "../../hooks/use-community-data";

/** Mapa de clave i18n → descriptor traducible (los `msg` deben ser estáticos). */
const BADGE_TEXT: Record<string, MessageDescriptor> = {
  "badge.first_enrollment.title": msg`First enrollment`,
  "badge.first_enrollment.description": msg`You enrolled in your first cohort.`,
  "badge.forum_first_post.title": msg`First post`,
  "badge.forum_first_post.description": msg`You wrote your first forum message.`,
  "badge.streak_7.title": msg`7-day streak`,
  "badge.streak_7.description": msg`You studied 7 days in a row.`,
  "badge.unknown.title": msg`Badge`,
  "badge.unknown.description": msg`Achievement unlocked.`,
};

function text(t: (d: MessageDescriptor) => string, key: string): string {
  const d = BADGE_TEXT[key];
  return d ? t(d) : key;
}

export const BadgesSection: Component = () => {
  const { t } = useI18n();
  const badges = useMyBadges();

  return (
    <section class="space-y-3" data-testid="badges-section">
      <h2 class="text-lg font-bold">{t(msg`Achievements`)}</h2>
      <Show
        when={(badges.data ?? []).length > 0}
        fallback={
          <p class="text-sm text-muted-foreground" data-testid="badges-empty">
            {t(msg`Earn badges by enrolling, posting in forums and keeping your streak.`)}
          </p>
        }
      >
        <ul class="flex flex-wrap gap-3" data-testid="badges-list">
          <For each={badges.data ?? []}>
            {(award) => {
              const meta = badgeMeta(award.badge_id ?? "");
              return (
                <li
                  data-testid="badge-item"
                  data-badge-id={award.badge_id}
                  class="flex w-40 flex-col items-center gap-1.5 rounded-2xl border border-border bg-surface p-3 text-center"
                >
                  <span class="text-3xl" aria-hidden="true">
                    {meta.emoji}
                  </span>
                  <span class="text-sm font-bold">{text(t, meta.titleKey)}</span>
                  <span class="text-xs text-muted-foreground">{text(t, meta.descriptionKey)}</span>
                </li>
              );
            }}
          </For>
        </ul>
      </Show>
    </section>
  );
};
